/** @typedef {"serif"|"sans"|"script"|"display"} FontCategory */
/** @typedef {{ family: string, category: FontCategory, weights: number[], italic: boolean, source: "google"|"system", fallback: string }} FontDefinition */

const SERIF_FALLBACK = "Georgia, 'Times New Roman', serif";
const SANS_FALLBACK = "Arial, Helvetica, sans-serif";
const SCRIPT_FALLBACK = "cursive";

/** @type {FontDefinition[]} */
export const FONT_CATALOG = [
  {
    family: "Playfair Display",
    category: "serif",
    weights: [400, 500, 600, 700, 800],
    italic: true,
    source: "google",
    fallback: SERIF_FALLBACK,
  },
  {
    family: "Cormorant Garamond",
    category: "serif",
    weights: [400, 500, 600, 700],
    italic: true,
    source: "google",
    fallback: SERIF_FALLBACK,
  },
  {
    family: "Cinzel",
    category: "display",
    weights: [400, 500, 600, 700],
    italic: false,
    source: "google",
    fallback: SERIF_FALLBACK,
  },
  {
    family: "EB Garamond",
    category: "serif",
    weights: [400, 500, 600, 700],
    italic: true,
    source: "google",
    fallback: SERIF_FALLBACK,
  },
  {
    family: "Libre Baskerville",
    category: "serif",
    weights: [400, 700],
    italic: true,
    source: "google",
    fallback: SERIF_FALLBACK,
  },
  {
    family: "Lora",
    category: "serif",
    weights: [400, 500, 600, 700],
    italic: true,
    source: "google",
    fallback: SERIF_FALLBACK,
  },
  {
    family: "Merriweather",
    category: "serif",
    weights: [300, 400, 700],
    italic: true,
    source: "google",
    fallback: SERIF_FALLBACK,
  },
  {
    family: "DM Serif Display",
    category: "display",
    weights: [400],
    italic: true,
    source: "google",
    fallback: SERIF_FALLBACK,
  },
  {
    family: "Marcellus",
    category: "display",
    weights: [400],
    italic: false,
    source: "google",
    fallback: SERIF_FALLBACK,
  },
  {
    family: "Great Vibes",
    category: "script",
    weights: [400],
    italic: false,
    source: "google",
    fallback: SCRIPT_FALLBACK,
  },
  {
    family: "Pinyon Script",
    category: "script",
    weights: [400],
    italic: false,
    source: "google",
    fallback: SCRIPT_FALLBACK,
  },
  {
    family: "Allura",
    category: "script",
    weights: [400],
    italic: false,
    source: "google",
    fallback: SCRIPT_FALLBACK,
  },
  {
    family: "Dancing Script",
    category: "script",
    weights: [400, 500, 600, 700],
    italic: false,
    source: "google",
    fallback: SCRIPT_FALLBACK,
  },
  {
    family: "Inter",
    category: "sans",
    weights: [300, 400, 500, 600, 700, 800],
    italic: false,
    source: "google",
    fallback: SANS_FALLBACK,
  },
  {
    family: "Lato",
    category: "sans",
    weights: [300, 400, 700, 900],
    italic: true,
    source: "google",
    fallback: SANS_FALLBACK,
  },
  {
    family: "Montserrat",
    category: "sans",
    weights: [300, 400, 500, 600, 700, 800],
    italic: true,
    source: "google",
    fallback: SANS_FALLBACK,
  },
  {
    family: "Open Sans",
    category: "sans",
    weights: [300, 400, 500, 600, 700],
    italic: true,
    source: "google",
    fallback: SANS_FALLBACK,
  },
  {
    family: "Poppins",
    category: "sans",
    weights: [300, 400, 500, 600, 700],
    italic: true,
    source: "google",
    fallback: SANS_FALLBACK,
  },
  {
    family: "Source Sans 3",
    category: "sans",
    weights: [300, 400, 600, 700],
    italic: true,
    source: "google",
    fallback: SANS_FALLBACK,
  },
  {
    family: "Raleway",
    category: "sans",
    weights: [300, 400, 500, 600, 700],
    italic: true,
    source: "google",
    fallback: SANS_FALLBACK,
  },
  {
    family: "DM Sans",
    category: "sans",
    weights: [400, 500, 700],
    italic: true,
    source: "google",
    fallback: SANS_FALLBACK,
  },
  {
    family: "Work Sans",
    category: "sans",
    weights: [300, 400, 500, 600, 700],
    italic: true,
    source: "google",
    fallback: SANS_FALLBACK,
  },
  {
    family: "Josefin Sans",
    category: "sans",
    weights: [300, 400, 600, 700],
    italic: true,
    source: "google",
    fallback: SANS_FALLBACK,
  },
  {
    family: "Oswald",
    category: "display",
    weights: [400, 500, 600, 700],
    italic: false,
    source: "google",
    fallback: SANS_FALLBACK,
  },
  {
    family: "Roboto",
    category: "sans",
    weights: [300, 400, 500, 700],
    italic: true,
    source: "google",
    fallback: SANS_FALLBACK,
  },
  {
    family: "Georgia",
    category: "serif",
    weights: [400, 700],
    italic: true,
    source: "system",
    fallback: SERIF_FALLBACK,
  },
  {
    family: "Times New Roman",
    category: "serif",
    weights: [400, 700],
    italic: true,
    source: "system",
    fallback: SERIF_FALLBACK,
  },
  {
    family: "Arial",
    category: "sans",
    weights: [400, 700],
    italic: true,
    source: "system",
    fallback: SANS_FALLBACK,
  },
  {
    family: "Verdana",
    category: "sans",
    weights: [400, 700],
    italic: true,
    source: "system",
    fallback: SANS_FALLBACK,
  },
];

const FONT_BY_FAMILY = new Map(FONT_CATALOG.map((font) => [font.family, font]));

export const DEFAULT_FONT_FAMILY = "Inter";

export const getFontDefinition = (family) => FONT_BY_FAMILY.get(family) || null;

export const isKnownFont = (family) => FONT_BY_FAMILY.has(family);

/** CSS font-family value with quoted family and generic fallback. */
export function cssFontFamily(family) {
  const definition = getFontDefinition(family);
  const fallback = definition?.fallback || SANS_FALLBACK;
  return `'${String(family || DEFAULT_FONT_FAMILY).replace(/'/g, "")}', ${fallback}`;
}

/** Nearest weight the family actually ships, so browsers don't synthesise bold. */
export function nearestWeight(family, weight) {
  const definition = getFontDefinition(family);
  const target = Number(weight) || 400;
  if (!definition) return target;
  return definition.weights.reduce((best, candidate) =>
    Math.abs(candidate - target) < Math.abs(best - target) ? candidate : best,
  );
}

/**
 * Curated heading/body pairings the AI and gallery pick from.
 * `accent` is optional (script for recipient names).
 */
export const FONT_PAIRINGS = [
  {
    id: "playfair-inter",
    heading: "Playfair Display",
    body: "Inter",
    accent: "Great Vibes",
    mood: ["elegant", "formal"],
  },
  {
    id: "cormorant-lato",
    heading: "Cormorant Garamond",
    body: "Lato",
    accent: "Pinyon Script",
    mood: ["elegant", "formal"],
  },
  {
    id: "cinzel-source",
    heading: "Cinzel",
    body: "Source Sans 3",
    accent: "Great Vibes",
    mood: ["formal", "royal"],
  },
  {
    id: "garamond-opensans",
    heading: "EB Garamond",
    body: "Open Sans",
    accent: "Allura",
    mood: ["formal", "academic"],
  },
  {
    id: "baskerville-lato",
    heading: "Libre Baskerville",
    body: "Lato",
    accent: null,
    mood: ["academic", "formal"],
  },
  {
    id: "dmserif-dmsans",
    heading: "DM Serif Display",
    body: "DM Sans",
    accent: null,
    mood: ["modern", "elegant"],
  },
  {
    id: "marcellus-raleway",
    heading: "Marcellus",
    body: "Raleway",
    accent: "Dancing Script",
    mood: ["elegant", "playful"],
  },
  {
    id: "montserrat-opensans",
    heading: "Montserrat",
    body: "Open Sans",
    accent: null,
    mood: ["modern", "bold"],
  },
  {
    id: "poppins-inter",
    heading: "Poppins",
    body: "Inter",
    accent: null,
    mood: ["modern", "minimal"],
  },
  {
    id: "inter-inter",
    heading: "Inter",
    body: "Inter",
    accent: null,
    mood: ["minimal", "modern"],
  },
  {
    id: "oswald-roboto",
    heading: "Oswald",
    body: "Roboto",
    accent: null,
    mood: ["bold", "modern"],
  },
  {
    id: "josefin-worksans",
    heading: "Josefin Sans",
    body: "Work Sans",
    accent: null,
    mood: ["minimal", "playful"],
  },
  {
    id: "lora-source",
    heading: "Lora",
    body: "Source Sans 3",
    accent: "Dancing Script",
    mood: ["elegant", "playful"],
  },
  {
    id: "merriweather-montserrat",
    heading: "Merriweather",
    body: "Montserrat",
    accent: null,
    mood: ["academic", "modern"],
  },
  {
    id: "cinzel-cormorant",
    heading: "Cinzel",
    body: "Cormorant Garamond",
    accent: "Pinyon Script",
    mood: ["royal", "formal"],
  },
  {
    id: "playfair-montserrat",
    heading: "Playfair Display",
    body: "Montserrat",
    accent: "Allura",
    mood: ["elegant", "modern"],
  },
  {
    id: "raleway-lato",
    heading: "Raleway",
    body: "Lato",
    accent: null,
    mood: ["minimal", "elegant"],
  },
  {
    id: "poppins-lora",
    heading: "Poppins",
    body: "Lora",
    accent: null,
    mood: ["modern", "playful"],
  },
  {
    id: "dmsans-dmsans",
    heading: "DM Sans",
    body: "DM Sans",
    accent: null,
    mood: ["minimal", "modern"],
  },
  {
    id: "garamond-montserrat",
    heading: "EB Garamond",
    body: "Montserrat",
    accent: "Great Vibes",
    mood: ["formal", "elegant"],
  },
];

export const getFontPairing = (id) =>
  FONT_PAIRINGS.find((pairing) => pairing.id === id) || FONT_PAIRINGS[0];

/**
 * Builds FontRef entries (with the weights/italics actually used) for a design.
 * @returns {{ family: string, source: string, weights: number[], italic: boolean }[]}
 */
export function buildFontRefs(elements = []) {
  const usage = new Map();
  elements.forEach((element) => {
    if (element.type !== "text" || !element.fontFamily) return;
    const entry = usage.get(element.fontFamily) || {
      weights: new Set(),
      italic: false,
    };
    entry.weights.add(nearestWeight(element.fontFamily, element.fontWeight));
    if (element.fontStyle === "italic") entry.italic = true;
    usage.set(element.fontFamily, entry);
  });
  return [...usage.entries()]
    .map(([family, entry]) => ({
      family,
      source: getFontDefinition(family)?.source || "system",
      weights: [...entry.weights].sort((a, b) => a - b),
      italic: entry.italic && Boolean(getFontDefinition(family)?.italic),
    }))
    .sort((a, b) => a.family.localeCompare(b.family));
}

/** Google Fonts css2 URL for the given refs, or null when none are Google fonts. */
export function buildGoogleFontsUrl(fontRefs = []) {
  const families = fontRefs
    .filter((ref) => ref.source === "google")
    .map((ref) => {
      const weights = ref.weights?.length ? ref.weights : [400];
      const name = ref.family.replace(/ /g, "+");
      if (ref.italic) {
        const tuples = [
          ...weights.map((weight) => `0,${weight}`),
          ...weights.map((weight) => `1,${weight}`),
        ];
        return `family=${name}:ital,wght@${tuples.join(";")}`;
      }
      return `family=${name}:wght@${weights.join(";")}`;
    });
  if (families.length === 0) return null;
  return `https://fonts.googleapis.com/css2?${families.join("&")}&display=block`;
}

const injectedStylesheets = new Set();

/**
 * Loads fonts into the current document so Konva can measure/draw them.
 * Resolves once every family/weight is available (or after a timeout).
 */
export async function loadFonts(fontRefs = [], { timeoutMs = 6000 } = {}) {
  if (typeof document === "undefined") return;
  const url = buildGoogleFontsUrl(fontRefs);
  if (url && !injectedStylesheets.has(url)) {
    injectedStylesheets.add(url);
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = url;
    link.dataset.htmlDesignerFonts = "true";
    document.head.appendChild(link);
  }
  if (!document.fonts?.load) return;

  const loads = fontRefs.flatMap((ref) =>
    (ref.weights?.length ? ref.weights : [400]).flatMap((weight) => {
      const styles = ref.italic ? ["normal", "italic"] : ["normal"];
      return styles.map((style) =>
        document.fonts.load(`${style} ${weight} 24px '${ref.family}'`),
      );
    }),
  );
  const timeout = new Promise((resolve) => setTimeout(resolve, timeoutMs));
  await Promise.race([Promise.allSettled(loads), timeout]);
}
